import { getApiUrl } from './apiConfig';

/**
 * @typedef {Object} YoutubeAuthState
 * @property {boolean} checked - Finished at least one status/validate request
 * @property {boolean} tokenValid - Refresh token works; uploads can proceed
 * @property {boolean} hasStoredToken - A refresh token exists in DB/env
 * @property {boolean} needsReconnect - Had a token but it is expired/revoked (show Reconnect)
 * @property {boolean} needsInitialConnect - No token yet (show Connect)
 * @property {boolean} showOAuthButton - Show Connect/Reconnect button in UI
 * @property {boolean} invalidGrant
 * @property {string|null} channelTitle
 */

const EMPTY_STATE = {
  checked: true,
  tokenValid: false,
  hasStoredToken: false,
  needsReconnect: false,
  needsInitialConnect: false,
  showOAuthButton: false,
  invalidGrant: false,
  channelTitle: null,
};

/**
 * Resolve whether YouTube OAuth is usable. Calls status + validate so a stale
 * DB token does not count as connected.
 * @param {string} [accountKey]
 * @returns {Promise<YoutubeAuthState>}
 */
/**
 * Poll until the server reports a working refresh token (OAuth may finish slightly after popup closes).
 * @param {string} [accountKey]
 * @param {{ attempts?: number, intervalMs?: number }} [options]
 */
export async function waitForYoutubeAuthReady(accountKey = 'default', options = {}) {
  const attempts = options.attempts ?? 12;
  const intervalMs = options.intervalMs ?? 750;
  let last = await fetchYoutubeAuthState(accountKey);
  for (let i = 0; i < attempts && !last.tokenValid; i += 1) {
    await new Promise((r) => setTimeout(r, intervalMs));
    last = await fetchYoutubeAuthState(accountKey);
  }
  return last;
}

export async function fetchYoutubeAuthState(accountKey = 'default') {
  try {
    const r = await fetch(
      `${getApiUrl()}/api/youtube/oauth/status?account_key=${encodeURIComponent(accountKey)}`
    );
    const d = await r.json().catch(() => ({}));

    let hasStoredToken = !!(d.hasStoredToken || d.connected);
    let tokenValid = d.tokenValid !== undefined ? !!d.tokenValid : false;
    let needsReconnect = !!d.needsReconnect;
    let invalidGrant = !!d.invalidGrant;

    if (hasStoredToken) {
      try {
        const validateRes = await fetch(
          `${getApiUrl()}/api/youtube/oauth/validate?account_key=${encodeURIComponent(accountKey)}`
        );
        if (validateRes.ok) {
          const validate = await validateRes.json().catch(() => ({}));
          if (validate.valid === true) {
            tokenValid = true;
            needsReconnect = false;
            invalidGrant = false;
          } else if (validate.valid === false) {
            tokenValid = false;
            needsReconnect = true;
            invalidGrant = !!validate.invalidGrant;
          }
        }
      } catch {
        /* older backend without /validate */
      }

      if (!tokenValid && !needsReconnect) {
        try {
          const setupRes = await fetch(`${getApiUrl()}/api/youtube/check-setup`);
          const setup = await setupRes.json().catch(() => ({}));
          if (setup?.credentials?.refresh_token_valid === false) {
            tokenValid = false;
            needsReconnect = true;
          }
        } catch {
          /* optional */
        }
      }
    } else if (d.connected && d.tokenValid === undefined) {
      hasStoredToken = true;
      tokenValid = false;
      needsReconnect = false;
    }

    const needsInitialConnect = !hasStoredToken && !tokenValid;
    if (hasStoredToken && !tokenValid) {
      needsReconnect = true;
    }

    return {
      checked: true,
      tokenValid,
      hasStoredToken,
      needsReconnect,
      needsInitialConnect,
      /** Reconnect only when a token exists but is invalid; Connect when never linked. */
      showOAuthButton: needsReconnect || needsInitialConnect,
      invalidGrant,
      channelTitle: d.channelTitle || null,
    };
  } catch {
    return { ...EMPTY_STATE, checked: true };
  }
}

/**
 * @param {YoutubeAuthState|null|undefined} state
 * @returns {boolean}
 */
export function shouldShowYoutubeReconnectButton(state) {
  if (!state?.checked) return false;
  return state.needsReconnect === true;
}

/**
 * @param {YoutubeAuthState|null|undefined} state
 * @returns {boolean}
 */
export function shouldShowYoutubeOAuthButton(state) {
  if (!state?.checked) return false;
  if (state.tokenValid) return false;
  return state.showOAuthButton;
}

/**
 * @param {YoutubeAuthState|null|undefined} state
 */
export function youtubeOAuthButtonLabel(state) {
  if (state?.needsReconnect) return 'Reconnect YouTube';
  return 'Connect YouTube';
}

/**
 * Short line above the OAuth button in upload modals.
 * @param {YoutubeAuthState|null|undefined} state
 * @returns {string|null}
 */
export function youtubeOAuthHintLine(state) {
  if (!state?.checked || state.tokenValid) return null;
  if (state.needsReconnect) {
    const ch = state.channelTitle ? ` (${state.channelTitle})` : '';
    return `YouTube upload access expired${ch}. Tap Reconnect YouTube to authorize again.`;
  }
  if (state.needsInitialConnect) {
    return 'No YouTube channel linked yet. Tap Connect YouTube to enable uploads.';
  }
  return null;
}

/**
 * @param {YoutubeAuthState|null|undefined} state
 */
export function youtubeOAuthButtonColor(state) {
  return state?.needsReconnect ? '#E65100' : '#FBC02D';
}
